import { FC, FormEvent, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';

import {
  IConfigIssue,
  IRoutingConfigVersion,
  PUBLISH_COMMENT_MAX_LENGTH,
  PUBLISH_COMMENT_MIN_LENGTH,
  describeIssue,
  getPublishConflict,
  isNotFoundError,
  isSameConfig,
  toEditorPolicies,
  useGetActiveRoutingConfigVersionQuery,
  usePublishVersion,
  validateDraft,
  validatePublishComment,
} from '@entities/routing/config-version';
import { useModal } from '@shared/components/Modal';

import { PUBLISH_COPY, getPublishMode } from './publishMode';
import PublishSummary from './PublishSummary';

interface IProps {
  // A draft (Publish) or an archived version (Activate) — the one that goes live.
  version: IRoutingConfigVersion;
}

// The publish / activate confirmation: what swaps, what blocks it, and the audit
// comment. Reads the active version itself rather than taking it as a prop — the
// shared ModalProvider freezes props at open time, and Reload must refresh what the
// operator is looking at.
const PublishModal: FC<IProps> = ({ version }) => {
  const { closeModal } = useModal();
  const copy = PUBLISH_COPY[getPublishMode(version)];
  const activeQuery = useGetActiveRoutingConfigVersionQuery();
  // A 404 is "no active config yet"; any other failure is "unavailable".
  const isActiveUnavailable = activeQuery.isError && !isNotFoundError(activeQuery.error);
  const active = isActiveUnavailable ? null : activeQuery.data?.data ?? null;
  const conflict = getPublishConflict(version, active);
  const isUnchanged = useMemo(
    () => active !== null && isSameConfig(version.policies, active.policies),
    [version.policies, active],
  );

  const policies = useMemo(() => toEditorPolicies(version.policies), [version.policies]);
  // The rules Save Draft enforces, re-run on the saved version: one that arrived
  // invalid (an empty policy, a broken limit) cannot go live.
  const localIssues = useMemo(() => validateDraft(policies), [policies]);

  const { publishVersion, isPublishing } = usePublishVersion();
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);
  // Issues from the last attempt, until the next one.
  const [serverIssues, setServerIssues] = useState<IConfigIssue[]>([]);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    commentRef.current?.focus();
  }, []);

  const issues = [...localIssues, ...serverIssues];
  const isBlocked = localIssues.length > 0;
  // The confirm stays disabled until the comment reaches the minimum length (QA: a one-
  // or two-character comment must not look submittable); the maximum is still checked
  // on submit, where the error can explain itself.
  const isCommentLongEnough = comment.trim().length >= PUBLISH_COMMENT_MIN_LENGTH;
  // "Anyway" whenever the archive side is uncertain: the base moved on, or is unknown.
  const confirmLabel = conflict || isActiveUnavailable ? `${copy.confirm} anyway` : copy.confirm;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const error = validatePublishComment(comment);
    if (error) {
      setCommentError(error);
      return;
    }
    setCommentError(null);
    setServerIssues([]);

    const result = await publishVersion(version.id, comment);
    if (result.status === 'failed') {
      setServerIssues(result.issues);
      return;
    }

    toast.success(`v${version.id} ${copy.done} — it is now the active version.`);
    closeModal();
  };

  return (
    <form className='routing-publish-modal' onSubmit={handleSubmit} noValidate>
      <h3 className='routing-publish-modal__title'>
        {copy.title} <span className='routing-version-number'>v{version.id}</span>
      </h3>

      <PublishSummary
        version={version}
        active={active}
        isActiveUnavailable={isActiveUnavailable}
        isReloading={activeQuery.isFetching}
        conflict={conflict}
        isUnchanged={isUnchanged}
        onReload={() => void activeQuery.refetch()}
      />

      {issues.length > 0 && (
        <div className='routing-publish-modal__issues'>
          <p>{isBlocked ? copy.blocked : copy.rejected}</p>
          <ul>
            {issues.map(issue => (
              <li key={`${issue.path}:${issue.message}`}>{describeIssue(issue, policies)}</li>
            ))}
          </ul>
        </div>
      )}

      <label className='routing-publish-modal__section-label' htmlFor='routing-publish-comment'>
        Comment · required
      </label>
      <textarea
        id='routing-publish-comment'
        ref={commentRef}
        rows={3}
        value={comment}
        onChange={event => setComment(event.target.value)}
        placeholder='Why this change'
        aria-invalid={commentError !== null}
        disabled={isPublishing}
      />
      <p className='routing-publish-modal__hint'>
        {PUBLISH_COMMENT_MIN_LENGTH}–{PUBLISH_COMMENT_MAX_LENGTH} characters. Goes into the audit
        trail with the author and timestamp.
      </p>
      {commentError && (
        <p className='routing-publish-modal__error' role='alert'>
          {commentError}
        </p>
      )}

      <div className='routing-publish-modal__btns'>
        <button type='button' className='secondary-btn' onClick={closeModal} disabled={isPublishing}>
          Cancel
        </button>
        <button
          type='submit'
          className='success-btn'
          disabled={isPublishing || isBlocked || !isCommentLongEnough}
        >
          {isPublishing ? copy.busy : confirmLabel}
        </button>
      </div>
    </form>
  );
};

export default PublishModal;
